import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { BisacCode } from "reedy-data/api";
import { SearchBar } from "~/components/search/search-bar";
import { useAnalytics } from "~/contexts/analytics-context";
import { BisacCodesTable } from "./bisac-codes-table";
import { useBisacCatalogueQuery } from "./hooks/use-bisac-catalogue-query";
import { useBisacSearchQuery } from "./hooks/use-bisac-search-query";
import { toBisacCodePrefix } from "./to-bisac-code-prefix";

const QUICK_SEARCH = "quick";

interface BisacCodesBrowserProps {
  submittedQuery: string;
  onSubmittedQueryChange: (query: string) => void;
  renderRowAction?: (code: BisacCode) => ReactNode;
  searchBarEnd?: ReactNode;
}

/** The caller owns the submitted query so it can live in the URL or in local state. */
export const BisacCodesBrowser = ({
  submittedQuery,
  onSubmittedQueryChange,
  renderRowAction,
  searchBarEnd,
}: BisacCodesBrowserProps) => {
  const { t } = useTranslation("components");
  const { gaEvent } = useAnalytics();
  const [inputQuery, setInputQuery] = useState(submittedQuery);
  const [openSubjectPrefix, setOpenSubjectPrefix] = useState<string | null>(
    null,
  );
  const catalogue = useBisacCatalogueQuery();
  const search = useBisacSearchQuery(submittedQuery);
  const isSearch = Boolean(submittedQuery);

  useEffect(() => {
    // syncs the controlled search input to the submitted query when it changes externally (e.g. navigation); the input is also locally editable, so it cannot be derived
    setInputQuery(submittedQuery);
  }, [submittedQuery]);

  // Keyed on the fetch time: a refetch of the same results keeps the same data object.
  const loggedSearchAtRef = useRef(0);
  useEffect(() => {
    if (!search.data || loggedSearchAtRef.current === search.dataUpdatedAt) {
      return;
    }
    loggedSearchAtRef.current = search.dataUpdatedAt;
    const codePrefix = toBisacCodePrefix(submittedQuery);
    gaEvent({
      type: "search_bisac_codes",
      payload: {
        query: submittedQuery,
        match_type: search.data.codes.some(
          ({ code }) => Boolean(codePrefix) && code.startsWith(codePrefix),
        )
          ? "code"
          : "heading",
        results_count: search.data.codes.length,
      },
    });
  }, [gaEvent, search.data, search.dataUpdatedAt, submittedQuery]);

  const submitQuery = (query: string) => {
    const trimmedQuery = query.trim();
    setOpenSubjectPrefix(null);
    if (trimmedQuery && trimmedQuery === submittedQuery) {
      search.refetch();
      return;
    }
    onSubmittedQueryChange(trimmedQuery);
  };

  const clearQuery = () => {
    setInputQuery("");
    submitQuery("");
  };

  return (
    <>
      <div role="search" className="flex space-x-2 mb-2 justify-between h-11">
        <SearchBar
          searchType={QUICK_SEARCH}
          searchTypeOptions={[
            {
              value: QUICK_SEARCH,
              label: t("components:bisacCodesTable.quickSearch"),
            },
          ]}
          searchQuery={inputQuery}
          onSearchQueryChange={setInputQuery}
          onSearchSubmit={() => submitQuery(inputQuery)}
          onClear={clearQuery}
          placeholder={t("components:bisacCodesTable.searchPlaceholder")}
          className="min-w-125 w-fit"
          showEnterHint
          showClearOnHover
        />
        {searchBarEnd}
      </div>
      <BisacCodesTable
        codes={isSearch ? search.data?.codes : catalogue.data?.codes}
        searchQuery={submittedQuery}
        openSubjectPrefix={openSubjectPrefix}
        onOpenSubjectPrefixChange={setOpenSubjectPrefix}
        isLoading={isSearch ? search.isLoading : catalogue.isLoading}
        isError={isSearch ? search.isError : catalogue.isError}
        renderRowAction={renderRowAction}
      />
    </>
  );
};
